import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  KeyRound, ShieldCheck, CreditCard, LogOut, Loader2, User as UserIcon, Crown, Check,
} from "lucide-react";

const inputCls = "w-full bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white outline-none focus:border-[var(--nik-blue)] transition-colors";

function Section({ icon: Icon, title, desc, children }) {
  return (
    <section className="bg-[#121824] border border-white/10 rounded-2xl p-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--nik-blue)] to-[#0047B3] flex items-center justify-center shrink-0">
          <Icon size={19} className="text-white" />
        </div>
        <div>
          <h2 className="font-display text-lg font-bold text-white">{title}</h2>
          <p className="text-xs text-slate-400">{desc}</p>
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

export default function Profile() {
  const { user, loading, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [showChangePw, setShowChangePw] = useState(false);

  // Kids PIN state
  const [newPin, setNewPin] = useState("");
  const [currentPin, setCurrentPin] = useState("");
  const [removePin, setRemovePin] = useState("");
  const [pinBusy, setPinBusy] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    api.get("/plans").then((r) => setPlan(r.data.find((p) => p.id === user.subscription_plan) || null)).catch(() => {});
  }, [user, loading]);

  if (loading || !user) {
    return <Layout><div className="pt-40 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div></Layout>;
  }

  const isPasswordAccount = user.auth_provider === "password";
  const hasPin = user.has_kids_pin;
  const pinValid = (p) => /^\d{4,6}$/.test(p);

  const doPin = async (kind) => {
    setPinBusy(kind);
    try {
      if (kind === "set") {
        if (!pinValid(newPin)) throw { msg: "PIN must be 4–6 digits" };
        await api.post("/kids/set-pin", { pin: newPin });
        toast.success("Kids PIN set");
      } else if (kind === "change") {
        if (!pinValid(newPin)) throw { msg: "New PIN must be 4–6 digits" };
        await api.post("/kids/change-pin", { current_pin: currentPin, new_pin: newPin });
        toast.success("Kids PIN changed");
      } else if (kind === "remove") {
        await api.request({ method: "DELETE", url: "/kids/pin", data: { pin: removePin } });
        toast.success("Kids PIN removed");
      }
      setNewPin(""); setCurrentPin(""); setRemovePin("");
      await refreshUser();
    } catch (err) {
      toast.error(err.msg || err.response?.data?.detail || "Failed");
    } finally {
      setPinBusy("");
    }
  };

  return (
    <Layout>
      <div className="pt-28 px-4 md:px-12 pb-16 max-w-3xl mx-auto" data-testid="profile-page">
        {/* Account header */}
        <div className="flex items-center gap-4 fade-up">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--nik-blue)] to-[#0047B3] flex items-center justify-center text-2xl font-bold text-white overflow-hidden">
            {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user.name?.[0] || "U").toUpperCase()}
          </div>
          <div>
            <h1 className="font-display text-3xl font-extrabold text-white">{user.name}</h1>
            <p className="text-sm text-slate-400">{user.email}</p>
            <div className="flex items-center gap-2 mt-1">
              {user.role === "admin" && <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded bg-[var(--nik-gold)] text-black">ADMIN</span>}
              <span className="text-[10px] font-medium tracking-wider px-2 py-0.5 rounded bg-white/5 text-slate-300 uppercase">{user.auth_provider} account</span>
            </div>
          </div>
        </div>

        <div className="mt-8 space-y-5">
          {/* Subscription */}
          <Section icon={CreditCard} title="Subscription" desc="Your current plan and billing.">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Crown size={22} className="text-[var(--nik-gold)]" />
                <div>
                  <div className="font-display font-bold text-white">{plan?.name || user.subscription_plan?.toUpperCase()}</div>
                  <div className="text-xs text-slate-400">
                    {plan ? `₹${plan.price_monthly}/mo • ${plan.quality} • ${plan.screens} screen${plan.screens > 1 ? "s" : ""}` : "Free tier"}
                  </div>
                </div>
              </div>
              <Link to="/plans" data-testid="manage-plan-btn"
                className="w-fit bg-white/10 hover:bg-white/20 border border-white/15 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
                Manage Plan
              </Link>
            </div>
            {plan?.features?.length > 0 && (
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check size={15} className="text-[var(--nik-glow)] mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Security */}
          <Section icon={KeyRound} title="Security" desc="Password and account access.">
            {isPasswordAccount ? (
              <button data-testid="open-change-password" onClick={() => setShowChangePw(true)}
                className="bg-[var(--nik-blue)] hover:bg-[var(--nik-blue-hover)] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
                Change Password
              </button>
            ) : (
              <p className="text-sm text-slate-400">This account signs in with Google. Use <Link to="/login" className="text-[var(--nik-glow)] hover:underline">Forgot Password</Link> on the sign-in page to also set a password.</p>
            )}
          </Section>

          {/* Kids PIN */}
          <Section icon={ShieldCheck} title="Kids PIN" desc="Controls who can leave the Kids safe zone.">
            {!hasPin ? (
              <div className="flex flex-col sm:flex-row gap-3">
                <input data-testid="kidspin-new" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                  maxLength={6} inputMode="numeric" placeholder="New PIN (4–6 digits)" className={inputCls + " sm:max-w-[220px]"} />
                <button data-testid="kidspin-set-btn" onClick={() => doPin("set")} disabled={pinBusy === "set"}
                  className="w-fit bg-[var(--nik-blue)] hover:bg-[var(--nik-blue-hover)] text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60">
                  {pinBusy === "set" && <Loader2 size={15} className="animate-spin" />} Set PIN
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-slate-400 mb-2">Change PIN</div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input data-testid="kidspin-current" value={currentPin} onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ""))}
                      maxLength={6} inputMode="numeric" placeholder="Current PIN" className={inputCls + " sm:max-w-[160px]"} />
                    <input data-testid="kidspin-new" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                      maxLength={6} inputMode="numeric" placeholder="New PIN" className={inputCls + " sm:max-w-[160px]"} />
                    <button data-testid="kidspin-change-btn" onClick={() => doPin("change")} disabled={pinBusy === "change"}
                      className="w-fit bg-[var(--nik-blue)] hover:bg-[var(--nik-blue-hover)] text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60">
                      {pinBusy === "change" && <Loader2 size={15} className="animate-spin" />} Change PIN
                    </button>
                  </div>
                </div>
                <div className="pt-3 border-t border-white/5">
                  <div className="text-xs text-slate-400 mb-2">Remove PIN (Kids mode will no longer be locked)</div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input data-testid="kidspin-remove" value={removePin} onChange={(e) => setRemovePin(e.target.value.replace(/\D/g, ""))}
                      maxLength={6} inputMode="numeric" placeholder="Enter current PIN" className={inputCls + " sm:max-w-[220px]"} />
                    <button data-testid="kidspin-remove-btn" onClick={() => doPin("remove")} disabled={pinBusy === "remove"}
                      className="w-fit bg-white/5 border border-white/15 hover:border-[var(--nik-live)] hover:text-[var(--nik-live)] text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-60">
                      {pinBusy === "remove" && <Loader2 size={15} className="animate-spin" />} Remove PIN
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Section>

          {/* Account actions */}
          <Section icon={UserIcon} title="Account" desc="Session and sign-in options.">
            <button data-testid="profile-logout-btn" onClick={async () => { await logout(); navigate("/"); }}
              className="flex items-center gap-2 bg-white/5 border border-white/15 hover:border-[var(--nik-live)] hover:text-[var(--nik-live)] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
              <LogOut size={16} /> Sign Out
            </button>
          </Section>
        </div>
      </div>
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </Layout>
  );
}
